import { Col, Row } from 'antd';
import { useParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { H2 } from 'components/new-ui/base/typography/styles';
import CreateUpdateNoteForm from 'components/new-ui/composed/note/CreateUpdateNoteForm';
import Note from 'components/new-ui/composed/note/Note';
import { NoteFormFields } from 'components/new-ui/composed/note/types';
import Notification from 'components/ui/base/notification/Notification';
import { ListPayload } from 'core/http/response';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import useSession from 'hooks/useSession';
import { createNote, deleteNote, getNotes, updateNote } from 'store/declarations/client';
import { getErrorMessage } from 'utils/getErrorMessage';
import { HEADER_AND_NAVBAR_CONTENT_HEIGHT_PX } from 'core/utils/constants';
import { DeclarationNote } from 'store/declarations/declaration';

const DeclarationNotes = () => {
    const { userInfo } = useSession();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const { declarationId } = useParams();
    const [notes, setNotes] = useState<ListPayload<DeclarationNote>>();
    const [submitting, setSubmitting] = useState(false);
    const [editingNoteId, setEditingNoteId] = useState<string | null>();

    const fetchNotes = useCallback(
        async (declarationId: string) => {
            showGlobalOverlay({
                type: 'LoadingOverlay',
            });

            try {
                const list = await getNotes(declarationId);
                setNotes(list);
            } catch (error) {
                Notification({
                    type: 'error',
                    messageTitle: 'Error',
                    description: 'Could not get notes from the server. Please refresh the page to try again.',
                    placement: 'top',
                });
            } finally {
                hideGlobalOverlay();
            }
        },
        [hideGlobalOverlay, showGlobalOverlay]
    );

    const handleSubmit = useCallback(
        async (values: NoteFormFields & { id?: string }): Promise<boolean> => {
            if (!declarationId) {
                Notification({
                    messageTitle: 'Error',
                    description: 'Could not get declaration data',
                    type: 'error',
                    placement: 'top',
                });
                return false;
            }

            setSubmitting(true);
            showGlobalOverlay({ type: 'LoadingOverlay' });
            try {
                if (editingNoteId) {
                    await updateNote(declarationId, editingNoteId, {
                        title: values.title,
                        note: values.content,
                    });

                    Notification({
                        messageTitle: 'Success',
                        description: 'Note updated',
                        type: 'success',
                        placement: 'top',
                    });
                } else {
                    await createNote(declarationId, {
                        note: values.content,
                        title: values.title,
                    });

                    Notification({
                        messageTitle: 'Success',
                        description: 'Note created',
                        type: 'success',
                        placement: 'top',
                    });
                }
                setEditingNoteId(null);
                await fetchNotes(declarationId);
                return true;
            } catch (error) {
                const errorMessage = getErrorMessage(error);
                Notification({
                    messageTitle: 'Error',
                    description: errorMessage || editingNoteId ? 'Could not update note' : 'Could not create note',
                    type: 'error',
                    placement: 'top',
                });
                return false;
            } finally {
                hideGlobalOverlay();
                setSubmitting(false);
            }
        },
        [declarationId, editingNoteId, fetchNotes, hideGlobalOverlay, showGlobalOverlay]
    );

    const handleDeleteNote = async (noteId: string) => {
        if (!declarationId) {
            Notification({
                messageTitle: 'Error',
                description: 'Could not get manifest id',
                type: 'error',
                placement: 'top',
            });

            return;
        }

        try {
            showGlobalOverlay({ type: 'LoadingOverlay' });
            await deleteNote(declarationId, noteId);
            Notification({
                messageTitle: 'Success',
                description: 'Note deleted',
                type: 'success',
                placement: 'top',
            });
            await fetchNotes(declarationId);
        } catch (error) {
            Notification({
                messageTitle: 'Error',
                description: 'Could not delete note',
                type: 'error',
                placement: 'top',
            });
        } finally {
            hideGlobalOverlay();
        }
    };

    const handleEdit = (noteId: string) => {
        setEditingNoteId(noteId);
    };

    const initialFormValues: NoteFormFields | undefined = useMemo(() => {
        if (!editingNoteId || !notes?.list) return undefined;
        const note = notes.list.find((_note) => _note.id === editingNoteId);
        return note ? { title: note.title ?? '', content: note.note ?? '' } : undefined;
    }, [editingNoteId, notes?.list]);

    useEffect(() => {
        if (declarationId) {
            fetchNotes(declarationId);
        }
    }, [declarationId, fetchNotes]);

    return (
        <div>
            <Row>
                <Col
                    lg={16}
                    style={{
                        // Display an inner vertical scroller for the notes container
                        overflowY: 'auto',
                        height: `calc(100vh - ${HEADER_AND_NAVBAR_CONTENT_HEIGHT_PX}px)`,
                        paddingRight: 24,
                    }}
                >
                    <H2 style={{ marginBottom: 32 }}>Notes</H2>
                    {notes?.list?.map((note) => (
                        <Note
                            note={{
                                content: note.note,
                                title: note.title,
                                id: note.id,
                                createdAt: note.createdAt,
                                createdBy: note.creator.fullName,
                            }}
                            isOwnNote={note.creator.id === userInfo?.individualId}
                            hideBorderBottom
                            onDelete={() => handleDeleteNote(note.id)}
                            onEdit={() => handleEdit(note.id)}
                        />
                    ))}
                </Col>

                <Col lg={8} style={{ paddingLeft: 24 }}>
                    <H2 style={{ marginBottom: 32 }}>{editingNoteId ? 'Edit Note' : 'Add Note'}</H2>
                    <CreateUpdateNoteForm
                        submitting={submitting}
                        initialValues={initialFormValues}
                        key={editingNoteId ?? 'create'}
                        onSubmit={handleSubmit}
                    />
                </Col>
            </Row>
        </div>
    );
};

export default DeclarationNotes;
